import { useMutation } from '@tanstack/react-query'
import { MustMatchValidator } from '@thinknimble/tn-forms'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { isAxiosError } from 'axios'
import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { AuthLayout } from 'src/components/auth-layout'
import { Button } from 'src/components/button'
import { ErrorMessage, ErrorsList } from 'src/components/errors'
import { Input } from 'src/components/input'
import { PasswordInput } from 'src/components/password-input'
import { userApi } from 'src/services/user'
import { AccountForm, AccountFormInputs, TAccountForm } from 'src/services/user/forms'
import { useAuth } from 'src/stores/auth'
import { GENERIC_REQUEST_ERROR } from 'src/utils/constants'
import { Trans, useTranslation } from 'react-i18next'
import { legalVersions } from 'src/content/legal'
import { LegalFooterLinks } from 'src/components/legal-footer-links'

function SignUpInner() {
  const { t } = useTranslation()
  const [errors, setErrors] = useState<string[]>([])
  const [termsAccepted, setTermsAccepted] = useState(false)
  const { changeToken, changeUserId, writeUserInStorage } = useAuth.use.actions()
  const { form, createFormFieldChangeHandler } = useTnForm<TAccountForm>()
  const navigate = useNavigate()

  const { mutate: createUser, isPending } = useMutation({
    mutationFn: userApi.csc.signup,
    onSuccess: (data) => {
      if (!data.token) throw new Error('Token should be returned on user creation')
      changeToken(data.token)
      changeUserId(data.id)
      // Store memberships so the root redirect can pick the first permitted module.
      writeUserInStorage(data)
      navigate('/')
    },
    onError(e: any) {
      console.error(e)
      if (isAxiosError(e)) {
        if (e.response?.data && typeof e.response.data === 'object') {
          setErrors(Object.values(e.response.data))
          return
        }
        if (e.message) {
          setErrors([e.message])
        } else {
          setErrors([GENERIC_REQUEST_ERROR])
        }
      }
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const data = {
      email: form.email.value,
      password: form.password.value,
      firstName: form.firstName.value,
      lastName: form.lastName.value,
      termsVersion: legalVersions.terms.version,
      privacyVersion: legalVersions.privacy.version,
    }
    const input = {
      ...data,
    }
    createUser(input as any)
  }

  return (
    <AuthLayout title={t('auth.signUpTitle')}>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            <div>
              <Input
                placeholder={t('auth.firstNamePlaceholder')}
                onChange={(e) => {
                  createFormFieldChangeHandler(form.firstName)(e.target.value)
                }}
                label={t('auth.firstNameLabel')}
                data-testid="first-name"
              />
              <ErrorsList errors={form.firstName.errors} />
            </div>
            <div>
              <Input
                placeholder={t('auth.lastNamePlaceholder')}
                onChange={(e) => {
                  createFormFieldChangeHandler(form.lastName)(e.target.value)
                }}
                label={t('auth.lastNameLabel')}
                data-testid="last-name"
              />

              <ErrorsList errors={form.lastName.errors} />
            </div>
          </div>
          <div>
            <Input
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              onChange={(e) => {
                createFormFieldChangeHandler(form.email)(e.target.value)
              }}
              label={t('auth.emailLabel')}
              data-testid="email"
            />
            <ErrorsList errors={form.email.errors} />
          </div>

          <PasswordInput
            placeholder={t('auth.passwordPlaceholder')}
            onChange={(e) => {
              createFormFieldChangeHandler(form.password)(e.target.value)
            }}
            label={t('auth.passwordLabel')}
            data-testid="password"
          />
          <ErrorsList errors={form.password.errors} />
          <PasswordInput
            id="confirmPassword"
            placeholder={t('auth.confirmPasswordPlaceholder')}
            onChange={(e) => {
              createFormFieldChangeHandler(form.confirmPassword)(e.target.value)
            }}
            label={t('auth.confirmPasswordLabel')}
            data-testid="confirm-password"
          />
          <ErrorsList errors={form.confirmPassword.errors} />
          <label className="flex items-start gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              className="mt-1"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="accept-terms"
            />
            <span>
              <Trans
                i18nKey="auth.acceptTerms"
                components={{
                  terms: (
                    <Link to="/legal/terms" target="_blank" className="text-accent underline" />
                  ),
                  privacy: (
                    <Link to="/legal/privacy" target="_blank" className="text-accent underline" />
                  ),
                }}
              />
            </span>
          </label>
          <Button
            type="submit"
            isLoading={isPending}
            disabled={isPending || !form.isValid || !termsAccepted}
            data-testid="submit"
          >
            {t('auth.signup')}
          </Button>
          {errors.length
            ? errors.map((e, idx) => <ErrorMessage key={idx}>{e}</ErrorMessage>)
            : null}
        </form>
      </div>
      <div className="m-4 flex self-center text-sm">
        <p className="mr-2">{t('auth.alreadyHaveAccount')}</p>

        <Link to="/log-in" className="font-bold text-primary hover:underline">
          {t('auth.loginLink')}
        </Link>
      </div>
      <LegalFooterLinks />
    </AuthLayout>
  )
}
const confirmPasswordValidator = {
  confirmPassword: new MustMatchValidator({
    message: 'Passwords must match',
    matcher: 'password',
  }),
}

export const SignUp = () => {
  return (
    <FormProvider<AccountFormInputs>
      formClass={AccountForm}
      formLevelValidators={confirmPasswordValidator}
    >
      <SignUpInner />
    </FormProvider>
  )
}
